import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import Card from '../ui/Card';

/**
 * @interface DataChartProps
 * @description Define as propriedades para o componente DataChart.
 * @property {any[]} data - Os dados a serem exibidos no gráfico. Cada objeto no array deve ter chaves correspondentes a `dataKeyX`, `dataKeysY`, `pieValueKey` e `pieNameKey`.
 * @property {string} title - O título do gráfico.
 * @property {'line' | 'bar' | 'area' | 'pie'} chartType - O tipo de gráfico a ser renderizado.
 * @property {string} [dataKeyX] - A chave do objeto de dados para o eixo X (para gráficos de linha, barra, área).
 * @property {string[]} [dataKeysY] - Um array de chaves do objeto de dados para o eixo Y (para gráficos de linha, barra, área). Permite múltiplas séries de dados.
 * @property {string} [pieValueKey] - A chave do objeto de dados para o valor do setor (para gráficos de pizza).
 * @property {string} [pieNameKey] - A chave do objeto de dados para o nome do setor (para gráficos de pizza).
 * @property {string[]} [colors] - Um array de cores personalizadas para as séries de dados. Se não fornecido, usará as cores padrão do tema.
 * @property {string} [className] - Classes CSS adicionais para estilizar o contêiner do gráfico.
 */
interface DataChartProps {
  data: any[];
  title: string;
  chartType?: 'line' | 'bar' | 'area' | 'pie';
  dataKeyX?: string;
  dataKeysY?: string[];
  pieValueKey?: string;
  pieNameKey?: string;
  colors?: string[];
  className?: string;
}

/**
 * @function DataChart
 * @description Componente para exibir gráficos de dados de forma moderna e responsiva, utilizando a biblioteca Recharts.
 * Adere ao Design System do projeto TugasCoins para cores e espaçamentos.
 *
 * @param {DataChartProps} props - As propriedades para configurar o gráfico.
 * @returns {JSX.Element} Um elemento JSX que renderiza o gráfico de dados.
 */
const DataChart: React.FC<DataChartProps> = ({
  data,
  title,
  chartType = 'line',
  dataKeyX,
  dataKeysY,
  pieValueKey,
  pieNameKey,
  colors,
  className = '',
}) => {
  const defaultColors = [
    '#d9a526', // Primária
    '#c38d09', // Accent
    '#e8c97d', // Secundária
    '#10B981', // Success
    '#EF4444', // Danger
    '#F59E0B', // Warning
  ];

  const chartColors = colors && colors.length > 0 ? colors : defaultColors;

  if (!data || data.length === 0) {
    return (
      <Card className={`p-6 bg-background border border-primary/20 text-text rounded-lg shadow-lg ${className}`}>
        <h3 className="text-xl font-semibold text-text mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-text-secondary">
          Não há dados para exibir o gráfico.
        </div>
      </Card>
    );
  }

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        if (!dataKeyX || !dataKeysY || dataKeysY.length === 0) {
          return <p className="text-danger">Configuração inválida para gráfico de linha: `dataKeyX` e `dataKeysY` são obrigatórios.</p>;
        }
        return (
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#4a4a4a" />
            <XAxis dataKey={dataKeyX} stroke="#f2eee3" tick={{ fill: '#f2eee3' }} />
            <YAxis stroke="#f2eee3" tick={{ fill: '#f2eee3' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#121212', border: '1px solid #d9a526', borderRadius: '8px' }}
              labelStyle={{ color: '#d9a526' }}
              itemStyle={{ color: '#f2eee3' }}
            />
            <Legend wrapperStyle={{ color: '#f2eee3', paddingTop: '10px' }} />
            {dataKeysY.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={chartColors[index % chartColors.length]}
                activeDot={{ r: 8 }}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        );
      case 'bar':
        if (!dataKeyX || !dataKeysY || dataKeysY.length === 0) {
          return <p className="text-danger">Configuração inválida para gráfico de barra: `dataKeyX` e `dataKeysY` são obrigatórios.</p>;
        }
        return (
          <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#4a4a4a" />
            <XAxis dataKey={dataKeyX} stroke="#f2eee3" tick={{ fill: '#f2eee3' }} />
            <YAxis stroke="#f2eee3" tick={{ fill: '#f2eee3' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#121212', border: '1px solid #d9a526', borderRadius: '8px' }}
              labelStyle={{ color: '#d9a526' }}
              itemStyle={{ color: '#f2eee3' }}
            />
            <Legend wrapperStyle={{ color: '#f2eee3', paddingTop: '10px' }} />
            {dataKeysY.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={chartColors[index % chartColors.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        );
      case 'area':
        if (!dataKeyX || !dataKeysY || dataKeysY.length === 0) {
          return <p className="text-danger">Configuração inválida para gráfico de área: `dataKeyX` e `dataKeysY` são obrigatórios.</p>;
        }
        return (
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#4a4a4a" />
            <XAxis dataKey={dataKeyX} stroke="#f2eee3" tick={{ fill: '#f2eee3' }} />
            <YAxis stroke="#f2eee3" tick={{ fill: '#f2eee3' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#121212', border: '1px solid #d9a526', borderRadius: '8px' }}
              labelStyle={{ color: '#d9a526' }}
              itemStyle={{ color: '#f2eee3' }}
            />
            <Legend wrapperStyle={{ color: '#f2eee3', paddingTop: '10px' }} />
            {dataKeysY.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={chartColors[index % chartColors.length]}
                fillOpacity={1}
                fill={`url(#color${index})`}
                strokeWidth={2}
              >
                <defs>
                  <linearGradient id={`color${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0} />
                  </linearGradient>
                </defs>
              </Area>
            ))}
          </AreaChart>
        );
      case 'pie':
        if (!pieValueKey || !pieNameKey) {
          return <p className="text-danger">Configuração inválida para gráfico de pizza: `pieValueKey` e `pieNameKey` são obrigatórios.</p>;
        }
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={pieValueKey}
              nameKey={pieNameKey}
              cx="50%"
              cy="50%"
              outerRadius={100}
              fill="#8884d8"
              label
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#121212', border: '1px solid #d9a526', borderRadius: '8px' }}
              labelStyle={{ color: '#d9a526' }}
              itemStyle={{ color: '#f2eee3' }}
            />
            <Legend wrapperStyle={{ color: '#f2eee3', paddingTop: '10px' }} />
          </PieChart>
        );
      default:
        return <p className="text-danger">Tipo de gráfico não suportado: {chartType}</p>;
    }
  };

  return (
    <Card className={`p-6 bg-background border-primary/20 text-text rounded-lg shadow-lg ${className}`}>
      <h3 className="text-xl font-semibold text-text mb-4">{title}</h3>
      <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default DataChart;